import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ConfirmResetPasswordInput } from 'aws-amplify/auth';
import { NgToastTranslocoService } from 'src/app/common/services/ng-toast-transloco/ng-toast-transloco.service';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { LoginService } from '../../services/login.service';
import { appId } from 'src/resources/app-config.json';
import { encryptionKey } from 'src/app/const/constant';
import { LoaderService } from 'src/app/common/services/loader/loader.service';

@Component({
  selector: 'ds-change-password',
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
})
export class ChangePasswordComponent {
  @Output() changePasswordEvent = new EventEmitter();
  @Output() resendCode = new EventEmitter();
  @Output() goToSignin = new EventEmitter();
  @Input() resetPasswordUsername: string = '';
  @Input() maskedEmail: string = '';
  changePasswordForm: FormGroup;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private toast: NgToastTranslocoService,
    private loginService: LoginService,
    private loaderService: LoaderService
  ) {
    this.changePasswordForm = this.formBuilder.group({
      newPassword: [
        '',
        [
          Validators.required,
          this.validateEnteredPassword(),
          Validators.pattern(/^\S*$/),
        ],
      ],
      confirmPassword: [
        '',
        [
          Validators.required,
          this.validateEnteredPassword(),
          this.verifyConfirmPasswordEntered('newPassword'),
        ],
      ],
      otp1: this.formBuilder.control('', [
        Validators.required,
        Validators.maxLength(1),
      ]),
      otp2: this.formBuilder.control('', [
        Validators.required,
        Validators.maxLength(1),
      ]),
      otp3: this.formBuilder.control('', [
        Validators.required,
        Validators.maxLength(1),
      ]),
      otp4: this.formBuilder.control('', [
        Validators.required,
        Validators.maxLength(1),
      ]),
      otp5: this.formBuilder.control('', [
        Validators.required,
        Validators.maxLength(1),
      ]),
      otp6: this.formBuilder.control('', [
        Validators.required,
        Validators.maxLength(1),
      ]),
    });
  }

  async changePassword() {
    let confirmCode =
      this.changePasswordForm.get('otp1')?.value +
      this.changePasswordForm.get('otp2')?.value +
      this.changePasswordForm.get('otp3')?.value +
      this.changePasswordForm.get('otp4')?.value +
      this.changePasswordForm.get('otp5')?.value +
      this.changePasswordForm.get('otp6')?.value;

    let encryptedNewPassword = await this.authService.encryptAes256Gcm(
      this.changePasswordForm.get('newPassword')?.value,
      encryptionKey
    );
    let changePasswordQuery: ConfirmResetPasswordInput = {
      username: this.resetPasswordUsername,
      newPassword: encryptedNewPassword,
      confirmationCode: confirmCode,
    };

    if (
      changePasswordQuery.confirmationCode.length === 6 &&
      changePasswordQuery.newPassword &&
      changePasswordQuery.confirmationCode &&
      !this.changePasswordForm.get('confirmPassword').errors &&
      !this.changePasswordForm.get('newPassword').errors
    ) {
      try {
        this.loaderService.setLoaderStateActive(
          true,
          'update_migration-status'
        );

        await this.authService.confirmPlayerPasswordReset(changePasswordQuery);

        // Update migration status to mark migration as completed
        try {
          await this.loginService.updateMigrationStatus({
            searchValue: this.resetPasswordUsername,
            isMigrationCompleted: true,
          });
          this.loaderService.setLoaderStateActive(
            false,
            'update_migration-status'
          );
          this.toast.toastSuccess(
          'MODULES.ACCOUNT_SECURITY.CHANGE_PASSWORD_LABELS.MESSAGES.PASSWORD_SUCCESSFULLY_CHANGED'
        );
        } catch (migrationError) {
          console.error('Failed to update migration status:', migrationError);
          this.loaderService.setLoaderStateActive(
            false,
            'update_migration-status'
          );
          this.toast.toastError(
            'MODULES.ACCOUNT_SECURITY.CHANGE_PASSWORD_LABELS.MESSAGES.UPDATE_MIGRATION_STATUS_FAILED'
          );
        }

        
        this.changePasswordEvent.emit();
      } catch (error) {
        this.loaderService.setLoaderStateActive(false, 'update_migration-status');
        this.toast.toastError('MODULES.ACCOUNT_SECURITY.CHANGE_PASSWORD_LABELS.MESSAGES.PASSWORD_CHANGE_FAILED');
      }
    } else {
      this.changePasswordForm.markAllAsTouched();
    }
  }

  goToSignIn() {
    this.goToSignin.emit();
  }

  verifyConfirmPasswordEntered(newPasswordRow: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      let newPassword = '';
      let confirmPassword = '';

      if (this.changePasswordForm) {
        confirmPassword = control && control.value ? control.value : '';
        newPassword =
          newPasswordRow &&
          this.changePasswordForm.get(newPasswordRow) &&
          this.changePasswordForm.get(newPasswordRow).value
            ? this.changePasswordForm.get(newPasswordRow).value
            : '';
      }

      if (newPassword !== confirmPassword)
        return { passwordValueMismatch: true };

      return null;
    };
  }

  validateEnteredPassword(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      let passwordValue = '';
      let passwordRegex =
        /(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#\$%\^&*()_+\-=\[\]{}|;:'",.<>?/~\\`])[\S\r]{8,}/;

      if (this.changePasswordForm) {
        passwordValue = control && control.value ? control.value : '';
      }

      if (passwordValue.length < 8) {
        return { mininumLengthRequiredError: true };
      }

      if (!passwordRegex.test(passwordValue)) {
        return { passwordStrengthError: true };
      }

      return null;
    };
  }

  confirmationCodeEntered() {
    if (
      (this.changePasswordForm.get('otp1').invalid &&
        this.changePasswordForm.get('otp1').touched) ||
      (this.changePasswordForm.get('otp2').invalid &&
        this.changePasswordForm.get('otp2').touched) ||
      (this.changePasswordForm.get('otp3').invalid &&
        this.changePasswordForm.get('otp3').touched) ||
      (this.changePasswordForm.get('otp4').invalid &&
        this.changePasswordForm.get('otp4').touched) ||
      (this.changePasswordForm.get('otp5').invalid &&
        this.changePasswordForm.get('otp5').touched) ||
      (this.changePasswordForm.get('otp6').invalid &&
        this.changePasswordForm.get('otp6').touched)
    ) {
      return true;
    }
  }

  resendConfirmationCode() {
    this.resendCode.emit();
  }

  moveToNext(
    event: KeyboardEvent,
    nextField: HTMLInputElement,
    prevField: HTMLInputElement
  ): void {
    const input = event.target as HTMLInputElement;

    // Move to the next field if a character is entered
    if (input.value.length === 1 && nextField) {
      nextField.focus();
    }

    // Move to the previous field if backspace is pressed and the current field is empty
    if (event.key === 'Backspace' && input.value.length === 0 && prevField) {
      prevField.focus();
    }
  }
}
